import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface PageProps {
  children: ReactNode;
  /** "default" ≈ 1200px reading width; "wide" 1440px; "full" edge to edge (canvas, logs). */
  width?: "narrow" | "default" | "wide" | "full";
  className?: string;
}

const widths = { narrow: "max-w-3xl", default: "max-w-[1200px]", wide: "max-w-[1440px]", full: "max-w-none" };

/** Standard page container. Every screen renders inside one. */
export function Page({ children, width = "default", className }: PageProps) {
  return (
    <div className={cn("mx-auto w-full px-4 pb-16 pt-6 sm:px-6 lg:px-10 lg:pt-8", widths[width], width === "full" && "px-0 pb-0 pt-0 sm:px-0 lg:px-0 lg:pt-0", className)}>
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Small line above the title (e.g. "Project · primecarers"). */
  eyebrow?: ReactNode;
  /** Right-aligned actions (primary action last). */
  actions?: ReactNode;
  /** Status chips etc. under the description. */
  meta?: ReactNode;
  className?: string;
}

/** Page title block. Titles use the display serif; keep them to a word or two. */
export function PageHeader({ title, description, eyebrow, actions, meta, className }: PageHeaderProps) {
  return (
    <header className={cn("mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="min-w-0">
        {eyebrow && <div className="mb-1.5 text-sm font-medium text-subtle">{eyebrow}</div>}
        <h1 className="font-display text-3xl font-semibold tracking-tight text-text">{title}</h1>
        {description && <p className="mt-1.5 max-w-2xl text-md text-muted">{description}</p>}
        {meta && <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

/** Section heading inside a page. */
export function Section({ title, description, actions, children, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("mt-10 first:mt-0", className)}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <h2 className="text-lg font-semibold text-text">{title}</h2>
          {description && <p className="mt-0.5 text-sm text-muted">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
